import React, { useEffect, useState } from 'react';
import { Target, Clock } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const MONTHS_BN = ['জানুয়ারি', 'ফেব্রুয়ারি', 'মার্চ', 'এপ্রিল', 'মে', 'জুন', 'জুলাই', 'আগস্ট', 'সেপ্টেম্বর', 'অক্টোবর', 'নভেম্বর', 'ডিসেম্বর'];

export const DriveGoal = ({ compact = false }) => {
  const { t } = useLanguage();
  const [g, setG] = useState(null);
  useEffect(() => {
    const load = () => fetch(`${process.env.REACT_APP_BACKEND_URL}/api/drive-goal`).then(r => r.json()).then(setG).catch(() => {});
    load();
    const id = setInterval(load, 30000);
    return () => clearInterval(id);
  }, []);
  if (!g) return null;

  const deadline = new Date(`${g.deadline}T23:59:59+06:00`);
  const pct = Math.min(100, (g.current / g.target) * 100);
  const daysLeft = Math.max(0, Math.ceil((deadline - new Date()) / 86400000));
  const target = g.target.toLocaleString('en-US');
  const month = deadline.toLocaleString('en-US', { month: 'long', timeZone: 'Asia/Dhaka' });
  const monthBn = MONTHS_BN[Number(g.deadline.slice(5, 7)) - 1];

  if (compact) {
    return (
      <div data-testid="drive-goal">
        <div className="mb-1.5 flex flex-wrap items-center justify-between gap-x-3 gap-y-1 text-xs">
          <span className="flex items-center gap-1.5 font-semibold text-white" data-testid="drive-goal-title">
            <Target className="h-4 w-4 text-[#FFD60A]" />
            {t({ en: `Goal: ${target} by ${month}`, bn: `লক্ষ্য: ${monthBn}-এর মধ্যে ${target}` })}
          </span>
          <span className="flex items-center gap-3 text-white/60">
            <span data-testid="drive-goal-progress"><b className="font-join text-white">{g.current.toLocaleString('en-US')}</b> / {target} · {pct.toFixed(1)}%</span>
            <span className="inline-flex items-center gap-1 font-bold text-[#FF3B3B]" data-testid="drive-goal-days-left"><Clock className="h-3.5 w-3.5" />{t({ en: `${daysLeft} days left`, bn: `আর ${daysLeft} দিন` })}</span>
          </span>
        </div>
        <div className="h-2.5 overflow-hidden rounded-full bg-white/10" role="progressbar" aria-valuenow={g.current} aria-valuemax={g.target} data-testid="drive-goal-bar">
          <div className="h-full rounded-full bg-gradient-to-r from-[#00E676] via-[#FFD60A] to-[#FF3B3B] transition-[width] duration-1000 ease-out" style={{ width: `${Math.max(pct, 1.5)}%` }} />
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-3xl border border-[#FFD60A]/30 bg-[#FFD60A]/[0.05] p-5 sm:p-6" data-testid="drive-goal">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <p className="font-join flex items-center gap-2 text-sm font-bold text-white sm:text-base" data-testid="drive-goal-title">
          <Target className="h-5 w-5 text-[#FFD60A]" />
          {t({ en: `Goal: ${target} new members by ${month}`, bn: `লক্ষ্য: ${monthBn}-এর মধ্যে ${target} নতুন সদস্য` })}
        </p>
        <span className="inline-flex items-center gap-1 rounded-full bg-[#FF3B3B] px-3 py-1 text-xs font-bold text-white" data-testid="drive-goal-days-left">
          <Clock className="h-3.5 w-3.5" /> {t({ en: `${daysLeft} days left`, bn: `আর ${daysLeft} দিন বাকি` })}
        </span>
      </div>
      <div className="h-4 overflow-hidden rounded-full bg-white/10" role="progressbar" aria-valuenow={g.current} aria-valuemax={g.target} data-testid="drive-goal-bar">
        <div className="h-full rounded-full bg-gradient-to-r from-[#00E676] via-[#FFD60A] to-[#FF3B3B] transition-[width] duration-1000 ease-out" style={{ width: `${Math.max(pct, 1.5)}%` }} />
      </div>
      <div className="mt-2 flex justify-between text-xs text-white/60">
        <span data-testid="drive-goal-progress"><b className="font-join text-white">{g.current.toLocaleString('en-US')}</b> / {target} {t({ en: 'joined', bn: 'যোগ দিয়েছেন' })}</span>
        <span>{pct.toFixed(1)}%</span>
      </div>
    </div>
  );
};
